/**
 * Browser-side API client.
 *
 * One place that knows how to talk to the API, so credentials, CSRF, and error
 * shape are handled identically everywhere. Screens never call fetch directly.
 */

/** In development Vite proxies /api to the Worker, so this stays same-origin. */
const BASE = import.meta.env.VITE_API_ORIGIN || '/api';

/**
 * The CSRF token, held in memory.
 *
 * It cannot be read from `document.cookie`. The API sets a `__Host-` prefixed
 * cookie on api.msarat.ai, and this app runs on app.msarat.ai — a different
 * host — so the cookie is invisible to this script even though the browser
 * faithfully sends it back with every credentialed request.
 *
 * So the token arrives in the JSON body of /auth/csrf instead. That preserves
 * the double-submit property: the cookie is attached automatically by the
 * browser, while the matching header value can only be obtained by reading a
 * response body, which CORS denies to any origin that is not allow-listed.
 */
let csrfToken = '';

/**
 * Guarantees a CSRF token before an unsafe request.
 *
 * A first-time visitor has never had a session, and registration and sign-in
 * are themselves unsafe methods, so the token has to be obtainable without one.
 */
async function ensureCsrfToken() {
  if (csrfToken) return csrfToken;
  try {
    const response = await fetch(`${BASE}/auth/csrf`, { credentials: 'include' });
    const body = await response.json();
    csrfToken = body?.token ?? '';
  } catch {
    csrfToken = '';
  }
  return csrfToken;
}

/**
 * An API failure carrying the server's error code and field details, so a form
 * can put a message next to the field that caused it.
 */
export class ApiError extends Error {
  constructor(code, message, details = [], status = 0) {
    super(message);
    this.code = code;
    this.details = details;
    this.status = status;
  }

  /**
   * Field errors keyed by field name, for form rendering.
   *
   * Pass the translator to render the server's machine-readable `code` in the
   * interface language; without it the server's own message is used, which is
   * Arabic. That fallback keeps callers working, but any screen a parent sees
   * should pass `t`.
   *
   * @param {(key: string, values?: Record<string, unknown>) => string} [t]
   * @param {Record<string, unknown>} [values] interpolation values, e.g. { min }
   */
  fieldErrors(t, values) {
    const out = {};
    for (const detail of this.details) {
      if (!detail.field || out[detail.field]) continue;
      out[detail.field] = translate(t, detail, values);
    }
    return out;
  }

  /** The form-level message, translated when a code is available. */
  localizedMessage(t, values) {
    const withCode = this.details.find((detail) => !detail.field && detail.code);
    return withCode ? translate(t, withCode, values) : this.message;
  }
}

/** Renders one validation detail, preferring its code over the server text. */
function translate(t, detail, values) {
  if (t && detail.code) {
    const key = `valid.${detail.code}`;
    const text = t(key, values);
    if (text !== key) return text;
  }
  return detail.message ?? '';
}

/**
 * Performs a request.
 *
 * `credentials: 'include'` is required for the session cookie to travel when
 * the API is on a different subdomain from the app.
 *
 * @param {string} path
 * @param {{ method?: string, body?: unknown }} [options]
 */
export async function request(path, options = {}) {
  const { method = 'GET', body, _retried = false } = options;

  const headers = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (!['GET', 'HEAD'].includes(method)) {
    headers['x-csrf-token'] = await ensureCsrfToken();
  }

  let response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // A network failure is not the user's fault and must not look like one.
    throw new ApiError('service_unavailable', 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجددًا.');
  }

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = payload?.error;

    // A rejected CSRF check usually means the in-memory token went stale, for
    // example after the cookie was rotated or expired. Refresh once and retry,
    // so the user is not shown a security error they cannot act on.
    if (response.status === 403 && error?.code === 'forbidden' && !_retried) {
      csrfToken = '';
      return request(path, { ...options, _retried: true });
    }

    throw new ApiError(
      error?.code || 'internal_error',
      error?.message || 'حدث خطأ غير متوقع.',
      error?.details || [],
      response.status,
    );
  }

  return payload;
}

/**
 * Uploads a file to a one-time ticket path.
 *
 * Separate from `request` because the body is raw bytes, not JSON: sending it
 * through the JSON path would stringify a File into "[object File]".
 *
 * @param {string} uploadPath the relative path returned by createUpload
 * @param {File} file
 */
export async function uploadFile(uploadPath, file) {
  const token = await ensureCsrfToken();

  let response;
  try {
    response = await fetch(`${BASE}${uploadPath}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': file.type || 'application/pdf', 'x-csrf-token': token },
      body: file,
    });
  } catch {
    throw new ApiError('service_unavailable', 'تعذّر الاتصال. تحقق من الإنترنت وحاول مجددًا.');
  }

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = payload?.error;
    throw new ApiError(
      error?.code || 'internal_error',
      error?.message || 'حدث خطأ غير متوقع.',
      error?.details || [],
      response.status,
    );
  }
  return payload;
}

export const api = {
  registerParent: (data) => request('/auth/parent/register', { method: 'POST', body: data }),
  loginParent: (data) => request('/auth/parent/login', { method: 'POST', body: data }),
  loginStudent: (data) => request('/auth/student/login', { method: 'POST', body: data }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  me: () => request('/auth/me'),
  verifyEmail: (token) => request(`/auth/verify?token=${encodeURIComponent(token)}`),
  listStudents: () => request('/students'),
  createStudent: (data) => request('/students', { method: 'POST', body: data }),
  updateMe: (data) => request('/auth/me', { method: 'PATCH', body: data }),
  updateStudent: (id, data) =>
    request(`/students/${encodeURIComponent(id)}`, { method: 'PATCH', body: data }),
  deleteStudent: (id) => request(`/students/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // The catalogue replaced uploading. A parent chooses a curriculum we have
  // already analysed and assigns it to a child.
  listCurricula: () => request('/curricula'),
  browseCatalogue: (params) => {
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) if (v) search.set(k, v);
    const q = search.toString();
    return request(`/catalogue${q ? `?${q}` : ''}`);
  },
  catalogueEntry: (id) => request(`/catalogue/${encodeURIComponent(id)}`),
  assignCurriculum: (studentId, bookId) =>
    request(`/students/${encodeURIComponent(studentId)}/curricula`, {
      method: 'POST',
      body: { bookId },
    }),
  unassignCurriculum: (id) => request(`/curricula/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};
